import React, { useCallback, useState } from "react";
import { Banner } from "@shopify/polaris";
import { connect } from "react-redux";
import { fetchCatalogPriceUpdateStatus } from "../../../redux/actions/settingsActions";

export const getCatalogPriceUpdateRunningMarkets = (markets) => {
  if (!Array.isArray(markets)) {
    return [];
  }
  return markets
    .map((market) => (typeof market === "string" ? market.trim() : ""))
    .filter(Boolean);
};

export const isCatalogPriceUpdateRunning = (markets) =>
  getCatalogPriceUpdateRunningMarkets(markets).length > 0;

export const isMarketCatalogPriceUpdateRunning = (marketName, runningMarkets) => {
  const normalizedMarketName = String(marketName || "").trim().toLowerCase();
  if (!normalizedMarketName) {
    return false;
  }
  return getCatalogPriceUpdateRunningMarkets(runningMarkets).some(
    (name) => name.toLowerCase() === normalizedMarketName,
  );
};

const CatalogPriceUpdateBanner = ({
  catalogPriceUpdateRunningMarkets = [],
  fetchCatalogPriceUpdateStatus,
  onStatusCheckResult,
}) => {
  const [isChecking, setIsChecking] = useState(false);

  const runningMarkets = getCatalogPriceUpdateRunningMarkets(
    catalogPriceUpdateRunningMarkets,
  );

  const handleCheckStatus = useCallback(() => {
    if (isChecking || typeof fetchCatalogPriceUpdateStatus !== "function") {
      return;
    }

    setIsChecking(true);

    fetchCatalogPriceUpdateStatus()
      .then((settings) => {
        const updatedMarkets = getCatalogPriceUpdateRunningMarkets(
          settings?.catalogPriceUpdateRunningMarkets,
        );
        const stillRunning = updatedMarkets.length > 0;

        if (typeof onStatusCheckResult === "function") {
          onStatusCheckResult(
            stillRunning
              ? `Catalog price update is still in progress for ${updatedMarkets.join(", ")}.`
              : "Catalog price update has completed.",
            false,
          );
        }
      })
      .catch(() => {
        if (typeof onStatusCheckResult === "function") {
          onStatusCheckResult(
            "Unable to check update status. Please try again.",
            true,
          );
        }
      })
      .finally(() => {
        setIsChecking(false);
      });
  }, [isChecking, fetchCatalogPriceUpdateStatus, onStatusCheckResult]);

  if (!runningMarkets.length) {
    return null;
  }

  const marketList = runningMarkets.join(", ");

  return (
    <div className="catalog-price-update-banner marketContactUsBanner">
      <Banner
        status="warning"
        title="Catalog price update in progress"
        action={{
          content: isChecking ? "Checking..." : "Check status",
          onAction: handleCheckStatus,
          disabled: isChecking,
        }}
      >
        <p>
          We are updating catalog prices for <strong>{marketList}</strong>.
          Please do not change product prices, markets, or other data until
          this finishes. Use <strong>Check status</strong> to see if it is
          done.
        </p>
      </Banner>
    </div>
  );
};

const mapStateToProps = (state) => ({
  catalogPriceUpdateRunningMarkets:
    state.settings.catalogPriceUpdateRunningMarkets || [],
});

const mapDispatchToProps = {
  fetchCatalogPriceUpdateStatus,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CatalogPriceUpdateBanner);
